import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Pause, Play, ShieldCheck, User } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Input } from '../../../shared/components/ui/Input.js';
import { Modal } from '../../../shared/components/ui/Modal.js';
import { serversApi, type Tenant, type TenantUser } from '../api.js';

interface Props {
  open: boolean;
  onClose: () => void;
  serverId: number;
  tenant: Tenant;
}

export function TenantUsersSheet({ open, onClose, serverId, tenant }: Props) {
  const [resetUser, setResetUser] = useState<TenantUser | null>(null);
  const qc = useQueryClient();

  const usersQuery = useQuery({
    queryKey: ['tenant-users', serverId, tenant.id],
    queryFn: () => serversApi.tenantUsers(serverId, tenant.id),
    enabled: open,
  });

  const toggleMut = useMutation({
    mutationFn: async ({ user, newStatus }: { user: TenantUser; newStatus: 'active' | 'inactive' }) =>
      serversApi.patchTenantUser(serverId, tenant.id, user.id, newStatus),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['tenant-users', serverId, tenant.id] });
      toast.success(
        vars.newStatus === 'inactive'
          ? `Usuário "${vars.user.email}" desativado`
          : `Usuário "${vars.user.email}" reativado`,
        {
          description:
            vars.newStatus === 'inactive'
              ? 'Sessões revogadas. Não conseguirá logar até reativar.'
              : undefined,
        },
      );
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={`Usuários — ${tenant.name}`} width={680} variant="sheet">
      <div className="mb-4 flex items-center gap-2 rounded-md bg-brand-50 px-3 py-2 text-[12px] text-brand-700">
        <ShieldCheck size={14} />
        <span>
          Você está visualizando os usuários internos do tenant <strong>{tenant.slug}</strong>. Pode
          resetar senhas e ativar/desativar usuários. Criação de novos usuários é feita pelo admin
          do tenant no próprio painel.
        </span>
      </div>

      {usersQuery.isLoading && (
        <div className="py-8 text-center text-[13px] text-n-500">Carregando usuários...</div>
      )}

      {usersQuery.error && (
        <div className="py-8 text-center text-[13px] text-danger-600">
          Falha ao carregar usuários. Servidor pode estar offline.
        </div>
      )}

      {usersQuery.data && usersQuery.data.length === 0 && (
        <div className="py-8 text-center text-[13px] text-n-500">
          Nenhum usuário cadastrado neste tenant.
        </div>
      )}

      {usersQuery.data && usersQuery.data.length > 0 && (
        <div className="overflow-hidden rounded-md border border-n-200">
          <table className="w-full">
            <thead className="border-b border-n-200 bg-n-50 text-left text-[11px] font-semibold uppercase text-n-500">
              <tr>
                <th className="px-3 py-2">Usuário</th>
                <th className="px-3 py-2">Perfil</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Último login</th>
                <th className="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {usersQuery.data.map((u) => {
                const inactive = u.status === 'inactive';
                return (
                  <tr key={u.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex h-[28px] w-[28px] items-center justify-center rounded-full bg-n-100 text-n-500">
                          <User size={14} />
                        </div>
                        <div className="leading-tight">
                          <div className="text-[12px] font-medium text-n-900">{u.name}</div>
                          <div className="text-[11px] text-n-500">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={
                          u.profile === 'admin'
                            ? 'rounded-pill bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700'
                            : 'rounded-pill bg-n-100 px-2 py-0.5 text-[10px] font-semibold text-n-600'
                        }
                      >
                        {u.profile === 'admin' ? 'ADMIN' : 'ATENDENTE'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={
                          inactive
                            ? 'rounded-pill bg-danger-50 px-2 py-0.5 text-[10px] font-semibold text-danger-700'
                            : 'rounded-pill bg-success-50 px-2 py-0.5 text-[10px] font-semibold text-success-700'
                        }
                      >
                        {inactive ? 'INATIVO' : 'ATIVO'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-[11px] text-n-500">
                      {u.last_login_at
                        ? new Date(u.last_login_at).toLocaleString('pt-BR')
                        : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setResetUser(u)}
                          title="Resetar senha"
                        >
                          <KeyRound size={12} />
                          Senha
                        </Button>
                        <Button
                          variant={inactive ? 'primary' : 'outline'}
                          size="sm"
                          loading={
                            toggleMut.isPending && toggleMut.variables?.user.id === u.id
                          }
                          onClick={() =>
                            toggleMut.mutate({
                              user: u,
                              newStatus: inactive ? 'active' : 'inactive',
                            })
                          }
                          title={inactive ? 'Reativar' : 'Desativar'}
                        >
                          {inactive ? <Play size={12} /> : <Pause size={12} />}
                          {inactive ? 'Ativar' : 'Desativar'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ResetPasswordModal
        user={resetUser}
        serverId={serverId}
        tenantId={tenant.id}
        onClose={() => setResetUser(null)}
      />
    </Modal>
  );
}

function ResetPasswordModal({
  user,
  serverId,
  tenantId,
  onClose,
}: {
  user: TenantUser | null;
  serverId: number;
  tenantId: number;
  onClose: () => void;
}) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const mut = useMutation({
    mutationFn: async () => {
      if (!user) return;
      await serversApi.resetTenantUserPassword(serverId, tenantId, user.id, password);
    },
    onSuccess: () => {
      toast.success(`Senha de "${user?.email}" resetada`, {
        description: 'Todas as sessões foram revogadas. Repasse a nova senha por canal seguro.',
      });
      setPassword('');
      setConfirm('');
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  const passwordsMatch = password.length >= 8 && password === confirm;

  return (
    <Modal
      open={!!user}
      onClose={() => {
        setPassword('');
        setConfirm('');
        onClose();
      }}
      title={`Resetar senha — ${user?.name ?? ''}`}
      width={420}
    >
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (passwordsMatch) mut.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <div className="rounded-md bg-warning-50 px-3 py-2 text-[12px] text-warning-700">
          A nova senha invalida TODAS as sessões ativas deste usuário. Repasse pelo canal seguro
          combinado com o cliente.
        </div>
        <Input
          label="Nova senha"
          type="text"
          required
          minLength={8}
          maxLength={120}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          hint="Mínimo 8 caracteres"
        />
        <Input
          label="Confirmar nova senha"
          type="text"
          required
          minLength={8}
          maxLength={120}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          {...(confirm.length > 0 && confirm !== password
            ? { error: 'Senhas não conferem' }
            : {})}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="danger" loading={mut.isPending} disabled={!passwordsMatch}>
            Resetar senha
          </Button>
        </div>
      </form>
    </Modal>
  );
}
